/**
 * <product-card> — docs/11 §10, §7.5.
 *
 * Two behaviours: swatch-driven image swap, and the wishlist toggle.
 *
 * Everything else the card does is CSS or a plain link. The add-to-cart path
 * is Dawn's <product-form> (docs/51 §6.1 keeps product-form.js), and the
 * quick-view button is a real link to the product page until Step 6.14
 * intercepts it — see product-card-actions.liquid.
 *
 * Wrapped in an IIFE on purpose. product-card.liquid emits this script tag,
 * and a caller that renders cards in a loop without passing
 * `skip_assets: forloop.first == false` would emit it 24 times. The browser
 * fetches once but EXECUTES once per tag, and a top-level `const` re-declared
 * that way is a SyntaxError that takes the whole file down. Inside a function
 * scope, a duplicate tag is merely wasteful.
 */
(function () {
  /**
   * Wishlist storage.
   *
   * D4 (docs/52 §4) — "localStorage only, or customer-metafield persistence?" —
   * is still open and is due before Step 10. This is deliberately the smallest
   * thing that works: one array of handles under one key, read and written
   * through two functions. Whichever way D4 lands, Step 10.4 replaces the two
   * functions and nothing else moves.
   */
  const WISHLIST_KEY = 'dramour:wishlist';

  function readWishlist() {
    try {
      const raw = window.localStorage.getItem(WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // Private mode, a full quota, or someone else's malformed value. A broken
      // wishlist must not take the card down with it.
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch {
      /* no-op — see above */
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
  }

  class ProductCard extends HTMLElement {
    connectedCallback() {
      /*
       * Looked up HERE, not inside bindSwatches, and that placement is a bug
       * fix rather than tidiness — docs/54 §1.-34.
       *
       * They started in `bindSwatches()`, which returns early when a card has
       * no swatches. Every SINGLE-VARIANT card therefore rendered its count
       * from Liquid and then never updated it: add the same tin twice and the
       * badge sat at 1 until the page was reloaded. Nine of twelve products
       * in this catalogue are single-variant, so the bug covered most of the
       * grid while the three products I was testing all worked.
       */
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.variantField = this.form?.querySelector('.product-variant-id');
      this.submitButton = this.form?.querySelector('[name="add"]');
      this.submitLabel = this.submitButton?.querySelector('.product-card__action-label');
      this.addLabel = this.submitLabel?.textContent;
      this.countBadge = this.querySelector('[data-cart-count]');

      this.bindWishlist();
      this.bindSwatches();
      this.bindCartCount();
    }

    disconnectedCallback() {
      this.cartUnsubscribe?.();
    }

    /* -- how many are already in the cart ---------------------------------- */

    /**
     * Keep this card's counts true as the cart changes anywhere — docs/54
     * §1.-34.
     *
     * Dawn publishes `cartUpdate` from product-form.js on add and from
     * cart.js on quantity edits and removals, so one subscription covers the
     * add from THIS card, the add from a card three rows down, and the
     * customer emptying the drawer.
     *
     * Guarded on the bus actually existing: `constants.js` and `pubsub.js`
     * are separate assets, and a card that throws in `connectedCallback`
     * takes its wishlist and swatches down with it.
     */
    bindCartCount() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCartCounts());
    }

    /* -- wishlist ---------------------------------------------------------- */

    bindWishlist() {
      this.wishlistButton = this.querySelector('[data-wishlist]');
      if (!this.wishlistButton) return;

      this.handle = this.wishlistButton.dataset.productHandle;
      this.syncWishlist();

      this.wishlistButton.addEventListener('click', () => {
        const handles = readWishlist();
        const at = handles.indexOf(this.handle);
        if (at === -1) handles.push(this.handle);
        else handles.splice(at, 1);
        writeWishlist(handles);
        this.syncWishlist();

        // A SAVE GETS A BEAT — 2026-10-27. The button used to fill on the same
        // frame with nothing else, so a save read like a mis-tap. Only on the
        // visitor's own tap and only when saving: syncWishlist() also runs on
        // load and on other cards' changes, where a pop would be noise.
        if (at === -1) {
          this.wishlistButton.classList.remove('is-popping');
          void this.wishlistButton.offsetWidth; // restart if tapped again mid-pop
          this.wishlistButton.classList.add('is-popping');
          this.wishlistButton.addEventListener('animationend', () => this.wishlistButton.classList.remove('is-popping'), { once: true });
        }
      });

      // Two cards for the same product can be on one page — a collection grid
      // and "recently viewed" below it. Without this the second one keeps
      // showing the stale state.
      document.addEventListener('wishlist:change', () => this.syncWishlist());
    }

    syncWishlist() {
      if (!this.wishlistButton) return;
      const saved = readWishlist().includes(this.handle);
      this.wishlistButton.setAttribute('aria-pressed', String(saved));

      // The accessible name has to change with the state. `aria-pressed` alone
      // announces "pressed" but leaves the label saying "Save …", which reads
      // as the wrong action once it is already saved.
      const key = saved ? 'removeLabel' : 'addLabel';
      const label = this.wishlistButton.dataset[key];
      if (label) this.wishlistButton.setAttribute('aria-label', label);
    }

    /* -- swatches ---------------------------------------------------------- */

    bindSwatches() {
      const inputs = this.querySelectorAll('.swatches__input');
      if (inputs.length === 0) return;

      this.mainImage = this.querySelector('.product-card__image--main img');
      this.originalSrc = this.mainImage?.getAttribute('src');
      this.originalSrcset = this.mainImage?.getAttribute('srcset');
      this.originalAlt = this.mainImage?.getAttribute('alt');
      /* The hover image is the lead colour's second photo until a swatch says
         otherwise — see swapSecond(). Absent below 1024, where it is not rendered. */
      this.mediaFrame = this.querySelector('.product-card__media');
      this.secondImage = this.querySelector('.product-card__image--second img');
      this.originalSecond = this.secondImage && {
        src: this.secondImage.getAttribute('src'),
        srcset: this.secondImage.getAttribute('srcset'),
        sizes: this.secondImage.getAttribute('sizes'),
        alt: this.secondImage.getAttribute('alt'),
      };

      /*
       * THE FORM HAS TO FOLLOW THE SWATCH — docs/54 §1.-33.
       *
       * Until this existed the swatch changed the photograph and nothing
       * else. On a card with a direct add-to-cart that is the worst kind of
       * bug: the customer picks Sand, sees Sand, adds to cart, and receives
       * Plum. Silent, and only discovered at the doorstep.
       *
       * The form and button are resolved in `connectedCallback` — scoped to
       * `this`, so a grid of twenty cards cannot cross-wire (the duplicate-id
       * failure swatches.liquid documents at length).
       */
      /*
       * THE COLOUR NAME — merchant, 2026-08-17.
       *
       * Written on `change` for the selection, and on pointer enter/leave for
       * the preview. `mouseenter`/`mouseleave` rather than `mouseover`: the
       * label contains an <img> and a couple of spans, so `mouseover` fires
       * again for every child crossed and `mouseout` fires while the pointer
       * is still inside the swatch — which flickers the name back to the
       * selected value mid-hover. The enter/leave pair does not bubble and
       * fires once per element, which is the behaviour this wants.
       *
       * Falling back to the CHECKED value on leave, not to the value that was
       * there before, so a hover that ends outside any swatch cannot strand
       * the wrong name under the row.
       */
      this.swatchName = this.querySelector('[data-swatch-name]');

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.swapImage(input);
          this.syncVariant(input);
          this.pulseSwatch(input);
        });

        const label = input.parentElement?.querySelector('.swatches__label');
        if (!label || !this.swatchName) return;
        label.addEventListener('mouseenter', () => this.showSwatchName(input.value));
        label.addEventListener('mouseleave', () => this.showSwatchName());
        // Keyboard parity: arrowing through a radio group fires `change`, but
        // Tab-focusing the checked one does not, so focus writes the name too.
        input.addEventListener('focus', () => this.showSwatchName(input.value));
      });

      /* The checked swatch at load is the one the form was rendered with, but
         say so out loud rather than assuming the two agree. */
      const checked = this.querySelector('.swatches__input:checked');
      if (checked) this.syncVariant(checked);
    }

    /**
     * Point the form at the chosen variant and make the button tell the truth
     * about it.
     *
     * A card can offer a colour that happens to be out of stock. Leaving the
     * button live would let the customer submit an unbuyable variant and meet
     * the error in the cart, which is a worse place to find out.
     */
    syncVariant(input) {
      if (!this.variantField) return;
      const id = input.dataset.variantId;
      if (id) this.variantField.value = id;

      /*
       * NOTHING ELSE TO SYNC. The quick-checkout button used to carry its own
       * `data-variant-id` and it went stale on every swatch change — it only
       * ever bought the right thing because its click handler preferred the
       * hidden input. It is Shopify's dynamic checkout button now
       * (`{{ form | payment_button }}`), which reads the form's `name="id"`
       * field directly, so the line above is the only place a variant is
       * written. One source, nothing to drift. — §1.-48
       */
      this.showSwatchName(input.value);
      this.showCount(parseInt(input.dataset.inCart, 10) || 0);

      if (!this.submitButton) return;
      const available = input.dataset.available !== 'false';
      this.submitButton.disabled = !available;
      if (this.submitLabel) {
        this.submitLabel.textContent = available
          ? this.addLabel
          : this.submitButton.dataset.soldOutLabel || this.addLabel;
      }
    }

    /**
     * Write the colour caption under the swatch row.
     *
     * With no argument it falls back to the checked swatch, which is what
     * "the pointer left" means and what the card should read at rest.
     */
    showSwatchName(value) {
      if (!this.swatchName) return;
      const name = value || this.querySelector('.swatches__input:checked')?.value;
      if (!name) return;
      /* THE LABEL IS PART OF THE CAPTION — merchant, 2026-08-18: "on the
         product card, follow the colour text same as that in product page".
         The product page's legend reads "Colour: Beige", so the card has to
         write the prefix back every time it rewrites the value, or the first
         hover strips it and the card silently reverts to the bare word. The
         prefix is rendered by Liquid into `data-option-label` so it stays
         translatable and stays whatever the merchant named the option. */
      const label = this.swatchName.dataset.optionLabel;
      this.swatchName.textContent = label ? `${label}: ${name}` : name;
    }

    /**
     * Acknowledge the act of choosing — merchant, 2026-08-18.
     *
     * The ring says which swatch is selected; this says that YOU selected it.
     * Driven from `change` rather than from `:checked` in CSS because one
     * swatch is already checked at render, and a `:checked` animation would
     * fire on every card on the page the moment it painted.
     *
     * The class is removed and re-added with a forced reflow between, which is
     * the only reliable way to restart a CSS animation on an element that may
     * still be running one — clicking two swatches quickly otherwise leaves
     * the second click unacknowledged. `prefers-reduced-motion` is handled in
     * the stylesheet, so this stays a pure state flag and the CSS decides
     * whether state means movement.
     */
    pulseSwatch(input) {
      const tile = input.parentElement?.querySelector('.swatches__thumb, .swatch');
      if (!tile) return;
      tile.classList.remove('is-picked');
      void tile.offsetWidth;
      tile.classList.add('is-picked');
    }

    /**
     * Paint one count onto the button — badge for sight, text for the
     * accessible name.
     *
     * `hidden` rather than a zero: a badge reading 0 on every card in a grid
     * is noise, and on this catalogue 11 of 12 cards would carry one.
     */
    showCount(count) {
      if (this.countBadge) {
        this.countBadge.textContent = count > 0 ? String(count) : '';
        this.countBadge.hidden = count <= 0;
      }

      /*
       * THE ACCESSIBLE NAME IS THE `aria-label`, AND NOTHING ELSE.
       *
       * An aria-label replaces the element's contents when the name is
       * computed, and this button has carried one since Step 5. A
       * visually-hidden count inside it — the obvious first attempt, and what
       * this did originally — measured as announced to nobody. So the count
       * is appended to the label itself and rebuilt from the stored base each
       * time, rather than accumulated onto the previous value.
       */
      if (this.submitButton) {
        const base = this.submitButton.dataset.addLabel;
        if (base) {
          this.submitButton.setAttribute('aria-label', count > 0 ? `${base}, ${count} in cart` : base);
        }
      }
    }

    /**
     * Re-derive every count on this card from the real cart.
     *
     * WHY REFETCH RATHER THAN INCREMENT. `cartUpdate` fires for adds from this
     * card, adds from any other card, quantity edits in the drawer and
     * removals — and its payload shape differs between publishers. Counting
     * locally would drift the moment the customer removed the item somewhere
     * else, and a wrong count is worse than none. `/cart.js` is small, cached
     * by the browser for the moment it takes, and always right.
     */
    async refreshCartCounts() {
      const inputs = [...this.querySelectorAll('.swatches__input')];
      if (!inputs.length && !this.countBadge) return;

      let items = [];
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
        if (!res.ok) return;
        items = (await res.json()).items || [];
      } catch {
        /* Offline or a blocked request: leave the last known counts rather
           than blanking them, which would read as "nothing in your cart". */
        return;
      }

      const byVariant = new Map();
      items.forEach((item) => byVariant.set(String(item.variant_id), item.quantity));

      inputs.forEach((input) => {
        const n = byVariant.get(String(input.dataset.variantId)) || 0;
        input.dataset.inCart = String(n);

        const dot = input.parentElement?.querySelector('[data-swatch-incart]');
        if (dot) dot.hidden = n <= 0;
        const dotText = input.parentElement?.querySelector('[data-swatch-incart-text]');
        if (dotText) dotText.textContent = n > 0 ? `, ${n} in cart` : '';
      });

      /* The button follows whichever swatch is selected — or the form's own
         variant on a single-variant card, which has no swatches at all. */
      const checked = this.querySelector('.swatches__input:checked');
      const id = checked?.dataset.variantId || this.variantField?.value;
      this.showCount(byVariant.get(String(id)) || 0);
    }

    swapImage(input) {
      if (!this.mainImage) return;
      const media = input.dataset.media;

      // A product whose variants share one image has no per-variant media, in
      // which case the correct behaviour is to leave the image alone rather
      // than blank it. docs/11 §10 marks the swap "inferred" on the reference,
      // so this is the conservative reading.
      if (!media) {
        this.mainImage.setAttribute('src', this.originalSrc || '');
        if (this.originalSrcset) this.mainImage.setAttribute('srcset', this.originalSrcset);
        if (this.originalAlt) this.mainImage.setAttribute('alt', this.originalAlt);
        this.restoreSecond();
        return;
      }

      // srcset wins over src whenever both are present, so it has to go or the
      // browser keeps serving the old image from the candidate list.
      this.mainImage.removeAttribute('srcset');
      this.mainImage.removeAttribute('sizes');
      this.mainImage.setAttribute('src', media);
      if (input.dataset.mediaAlt) this.mainImage.setAttribute('alt', input.dataset.mediaAlt);
      this.swapSecond(input);
      this.settleImage(media);
    }

    /**
     * THE NEW COLOUR ARRIVES INSTEAD OF APPEARING — 2026-10-27.
     *
     * The old photo stays painted until the new one decodes, then it was
     * replaced on one frame: on a phone, tapping Black turned a tan bag black
     * with no seam. Once the new photo has DECODED it rises from 40% to full
     * over 240ms, so the change reads as the swatch's doing and never shows a
     * blank frame. On the <img>, not its wrapper: the wrapper's opacity is the
     * desktop hover crossfade and must stay the CSS's. A newer tap supersedes
     * an older one, and reduced motion keeps the instant swap.
     */
    settleImage(media) {
      const img = this.mainImage;
      if (!img || typeof img.animate !== 'function') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const fade = () => {
        if (img.getAttribute('src') !== media) return;
        img.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 240, easing: 'cubic-bezier(0.23, 1, 0.32, 1)' });
      };
      if (typeof img.decode === 'function') img.decode().then(fade, () => {});
      else fade();
    }

    /**
     * THE HOVER IMAGE FOLLOWS THE SWATCH TOO — merchant, 2026-10-22: "if i
     * select say white, it shows black side view".
     *
     * Only the main image used to change, so on a pointer device hovering a
     * card after picking White cross-faded to `product.media[1]` — the lead
     * colour's side view. Each swatch now carries its own colour's second
     * photo (swatches.liquid). A colour without one switches the hover off for
     * as long as it is selected: no second photo is better than the wrong one.
     */
    swapSecond(input) {
      if (!this.secondImage) return;
      const src = input.dataset.mediaSecond;
      this.mediaFrame?.classList.toggle('product-card__media--has-second', Boolean(src));
      if (!src) return;
      this.secondImage.removeAttribute('srcset');
      this.secondImage.removeAttribute('sizes');
      this.secondImage.setAttribute('src', src);
      if (input.dataset.mediaSecondAlt) this.secondImage.setAttribute('alt', input.dataset.mediaSecondAlt);
    }

    restoreSecond() {
      if (!this.secondImage || !this.originalSecond) return;
      const { src, srcset, sizes, alt } = this.originalSecond;
      this.secondImage.setAttribute('src', src || '');
      if (srcset) this.secondImage.setAttribute('srcset', srcset);
      if (sizes) this.secondImage.setAttribute('sizes', sizes);
      if (alt) this.secondImage.setAttribute('alt', alt);
      this.mediaFrame?.classList.add('product-card__media--has-second');
    }
  }

  if (!customElements.get('product-card')) {
    customElements.define('product-card', ProductCard);
  }

  /*
   * THE CARDS' "BUY IT NOW" ARRIVES ON THE FIRST SCROLL OR TAP — 2026-10-06.
   * -------------------------------------------------------------------------
   * Shopify starts its wallet code (portable-wallets, then the checkout-web
   * chunks) on DOMContentLoaded as soon as ONE <shopify-accelerated-checkout>
   * is in the document. The header's menu cards put four on every page, so
   * every page view — the contact page included — downloaded about 1 MB of
   * checkout JavaScript before the visitor had done anything. Measured on
   * live: home 2,989 KiB with it, 2,011 KiB without.
   *
   * product-card-actions.liquid now ships each card's button inside a
   * <template>. On the visitor's first scroll, tap, key or mouse move they
   * are all put in place, and Shopify's own MutationObserver (in
   * content_for_header) starts the wallets then. Cards rendered later —
   * filters, sorting, quick view, recently viewed — are expanded as they
   * arrive. The slot keeps its height meanwhile (component-product.css), so
   * nothing moves. The product page's own buy box is NOT deferred.
   */
  if (!window.__dramourDeferredCheckout) {
    window.__dramourDeferredCheckout = true;
    const SELECTOR = '[data-deferred-checkout] > template';
    const expand = (root) => {
      if (!root.querySelectorAll) return;
      root.querySelectorAll(SELECTOR).forEach((template) => template.replaceWith(template.content.cloneNode(true)));
    };
    /* `scroll` WITHOUT capture: an element's scroll event does not bubble,
       but in the capture phase every one of them reaches window — and the
       homepage's category row scrolls itself 1.4s after load (measured), which
       expanded every button with nobody there. Only the page's own scroll
       counts as the visitor. */
    const inputs = ['pointerdown', 'keydown', 'touchstart', 'mousemove'];
    const start = () => {
      inputs.forEach((name) => window.removeEventListener(name, start, true));
      window.removeEventListener('scroll', start);
      expand(document);
      new MutationObserver((records) => {
        records.forEach((record) =>
          record.addedNodes.forEach((node) => {
            if (node.nodeType === 1) expand(node);
          }),
        );
      }).observe(document.body, { childList: true, subtree: true });
    };
    inputs.forEach((name) => window.addEventListener(name, start, { capture: true, passive: true }));
    window.addEventListener('scroll', start, { passive: true });
  }
})();

/*
 * THE QUICK-CHECKOUT HANDLER IS GONE — merchant, 2026-08-17, §1.-48.
 *
 * It added the variant with `/cart/add.js` and then sent the browser to
 * `/checkout`, which checks out the WHOLE cart — the merchant's complaint
 * exactly. `{{ form | payment_button }}` replaced it in
 * `product-card-actions.liquid`: Shopify's dynamic checkout creates a
 * checkout for the single line in that form and never touches the cart, and
 * it needs no JavaScript from us.
 *
 * Deleted rather than left inert. A delegated document-level click listener
 * matching a selector nothing renders is invisible dead weight, and the next
 * person to add `data-card-checkout` to anything would silently re-enable
 * whole-cart checkout.
 */

/*
 * THE TAPPED PHOTO BECOMES THE PRODUCT PAGE'S PHOTO — 2026-10-27.
 *
 * base.css turns on cross-document view transitions and names the product
 * page's first gallery photo `pdp-hero`. Naming the tapped card's photo the
 * same on click is what makes the browser grow one into the other instead of
 * cross-fading two unrelated pictures. Browsers without view transitions
 * ignore the property, so this is inert there.
 *
 * A name must be unique on the page or the browser skips the transition
 * altogether, so any earlier holder is cleared first: the card tapped last
 * time (this page can come back from the back/forward cache still naming it),
 * and, when this IS a product page, its own gallery photo, which the CSS names
 * and an inline `none` un-names. Delegated and module-level, so it runs once
 * however many cards render; capture phase, so it lands before navigation.
 */
(() => {
  if (window.__dramourCardMorphBound) return;
  window.__dramourCardMorphBound = true;
  if (!window.CSS || !CSS.supports('view-transition-name: a')) return;

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('.product-card a[href*="/products/"]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const photo = link.closest('.product-card')?.querySelector('.product-card__image--main img');
      if (!photo) return;

      document.querySelectorAll('[data-vt-hero]').forEach((el) => {
        el.style.viewTransitionName = '';
        delete el.dataset.vtHero;
      });
      document
        .querySelectorAll('.product__media-list > .product__media-item:first-child .product-media-container img')
        .forEach((el) => (el.style.viewTransitionName = 'none'));

      photo.style.viewTransitionName = 'pdp-hero';
      photo.dataset.vtHero = '';

      // The product page shows this already-cached photo behind its gallery
      // image until the full-size one loads — snippets/pdp-hero-handoff.liquid.
      try {
        const handle = new URL(link.href, location.href).pathname.split('/products/')[1];
        sessionStorage.setItem('dramour-vt-hero', JSON.stringify({ handle, src: photo.currentSrc || photo.src }));
      } catch (e) {
        /* storage blocked: the transition still runs, just without the hand-off */
      }
    },
    true
  );
})();
